import { motion } from 'framer-motion';
import { SectionShell } from './SectionShell';

/**
 * The Problem — what every IP's day looks like today.
 *
 * Three "pain pillars" sourced from CLAUDE.md's day-in-the-life framing:
 * email-driven chasing, document drafting from scratch, deadline juggling
 * across 20–40 simultaneous cases. No product imagery — just the pain.
 */

const PAINS: { id: string; eyebrow: string; title: string; body: string }[] = [
  {
    id: 'manual',
    eyebrow: '01 · Manual',
    title: 'Documents drafted from scratch',
    body: 'Statutory reports, decision procedure notices, creditor circulars, Statement of Affairs follow ups. Each one assembled in Word from a previous case, with copy paste errors and version drift the norm',
  },
  {
    id: 'inbox',
    eyebrow: '02 · Inbox',
    title: 'Every case runs through email',
    body: 'Directors chased for bank statements. Creditors emailed updates. Companies House confirmations forwarded in. An IP juggles 20 to 40 live cases at once, and the case file lives in their inbox',
  },
  {
    id: 'audit',
    eyebrow: '03 · Audit',
    title: 'No native evidence trail',
    body: 'Compliance under IPA inspection is reconstructed at year end from email threads, file shares and folder structures. The audit trail is forensic, not native to the workflow',
  },
];

export function TheProblem() {
  return (
    <SectionShell
      id="problem"
      index={2}
      eyebrow="The problem"
      title={
        <>
          Every case is built{' '}
          <span className="text-brand-bright">from scratch</span>
        </>
      }
      intro="Roughly 80% of the workflow is the same across every UK case. But today, that shared core is reassembled by hand each time, on top of folder structures, Word templates and email threads that nobody ever consolidated"
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {PAINS.map((p, i) => (
          <PainCard key={p.id} pain={p} index={i} />
        ))}
      </div>
    </SectionShell>
  );
}

function PainCard({
  pain,
  index,
}: {
  pain: (typeof PAINS)[number];
  index: number;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ y: -3 }}
      transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        delay: index * 0.08,
      }}
      className="pitch-card pitch-card-interactive flex flex-col gap-4 rounded-2xl p-7 sm:p-8"
    >
      <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
        {pain.eyebrow}
      </span>
      <h3 className="text-balance text-[22px] font-semibold leading-snug text-ink-strong">
        {pain.title}
      </h3>
      <p className="text-[14px] leading-relaxed text-ink-muted">{pain.body}</p>
    </motion.article>
  );
}
